import _ from 'lodash';
import {
  SHOP,
  CATALOG,
  SUPPORT,
  GLOWFORGE_LANDING_PAGE,
  LOGIN,
} from 'app/route-constants';

/**
 * Navigation Configuration
 * 
 * This file defines the tab/route configuration for the top level
 * navbar across Glowforge settings.
 *
 * There are three Navigations:
 * Marketing (glowforge.com) (Being moved to dotcom repo)
 * Settings (glowforge.com/settings) Unrestricted version
 * Settings (glowforge.com/settings) Restricted version
 * 
 * The Nav bar itself is split in to two parts: top level routes and
 * user routes. On desktop these are distinct menus, but on mobile
 * they are in the same drop down.
 *
 * --------------------------------------------------------------------
 * Feature flags and route-constants:
 *
 * We get constants/flags from three places:
 *  1. app/route-constants.js
 *  2. sharedEnv object defined globally/window
 *  3. Feature flags passed by the backend through api call to /storefront/api/features
 *     These flags come from flip-flop or launchdarkly. See app/common/api/common-api.js
 *
 * --------------------------------------------------------------------
 * Using this config:
 *
 * This file contains configurations objects for routes, and getters
 * for selecting and ordering of routes.
 *
 * Individual tab/route config objects are of the following structure:
 * {
 *   @param {boolean}   local    Whether the route exists within our app. Used to determine whether
 *                            to use React Router <Link /> (can't be an external link) or <a />
 *   @param {string} text     Link text
 *   @param {string} url      relative url if local link, absolute/relative if external
 *   @param {boolean}   disabled If false, the route is visible.
 *   @param {boolean}   openInNewTab If !local, Should we open in a new window? Uses target="_blank"
 *   @param {boolean}   mobileOnly Only render the route on mobile devices/screen widths,
 *                              used for displaying the user menu in the mobile dropdown.
 * }
 */

/**
 * Separator tab
 * Defines where a visual separation should occur in the nav.
 */
export const separator = {
  separator: true,
  mobileOnly: true,
};

/**
 * Defines navigation routes (tabs) for the Marketing navbar
 */
export const marketingRoutes = {
  home: {
    local: false,
    text: 'Home',
    url: GLOWFORGE_LANDING_PAGE.BASE_URI,
  },
  products: {
    local: false,
    text: 'Products',
    url: SHOP.BASE_PRODUCT_URL,
  },
  community: {
    local: false,
    text: 'Community',
    url: 'https://community.glowforge.com',
  },
};

/**
 * User menu routes.
 */
export const userRoutes = {
  referrals: {
    local: false,
    text: 'Referrals',
    url: 'https://glowforge.com/b/refer-a-friend',
  },
  login: {
    local: false,
    text: 'Sign in',
    url: `${typeof sharedEnv === 'undefined' ? '' : sharedEnv.AUTH_SERVER}${LOGIN.BASE_URI}`,
  },
};

/**
 * Secondary links for user menu.
 */
export const userSecondaryLinks = [
  { ...userRoutes.referrals },
];

/**
 * @param {Object} features These are feature flags passed by the backend.
 *                          They either come from flip-flop or launchdarkly.
 */
export const getAppRoutes = ({
  OLD_SHOP,
  OLD_CATALOG,
  SUPPORT_PAGE_ENABLED,
  GFCORE_SITE_URL,
  COMMUNITY_NEW_TAB,
}) => ({
  home: {
    local: false,
    text: 'Home',
    url: GFCORE_SITE_URL || 'https://glowforge.com',
  },
  catalog: {
    local: !OLD_CATALOG,
    text: 'Catalog',
    url: (OLD_CATALOG && OLD_CATALOG.URL) || CATALOG.BASE_URI,
    disabled: !CATALOG.ENABLED && !OLD_CATALOG,
  },
  shop: {
    local: !OLD_SHOP,
    text: 'Shop',
    url: (OLD_SHOP && OLD_SHOP.URL) || SHOP.BASE_URL,
    disabled: !SHOP.ENABLED && !OLD_SHOP,
  },
  community: {
    local: false,
    text: 'Community',
    url: 'https://community.glowforge.com',
    openInNewTab: COMMUNITY_NEW_TAB,
  },
  support: {
    local: false,
    text: 'Support',
    url: SUPPORT.BASE_URI,
    disabled: !SUPPORT_PAGE_ENABLED,
  },
});

/**
 * @param {Object} features These are feature flags passed by the backend.
 *                          They either come from flip-flop or launchdarkly.
 * @returns {Array} Array of tab objects in Marketing Nav order
 */
export const getMarketingRoutes = ({
  COMMUNITY_NEW_TAB,
}) => [
  marketingRoutes.home,
  marketingRoutes.products,
  {
    ...marketingRoutes.community,
    openInNewTab: COMMUNITY_NEW_TAB,
  },
  marketingRoutes.gallery,
];

/**
 * getNavigationTabs builds an array of nav objects based on options it is passed.
 *
 * @param {Object of constants} features An object containing constants.
 * @param {boolean} hideNavLinks If true, hide all marketing nav links except for user specific routes
 * @param {boolean} isRestricted If true, hide everything but home link
 * @param {boolean} useMarketingNav Use the marketing site nav otherwise shows the shop/catalog nav
 * @param {Object} user Currently logged in User
 */
export const getNavigationTabs = (
  features,
  hideNavLinks,
  isRestricted,
  useMarketingNav,
  user
  ) => {
  if (useMarketingNav) {
    if (!hideNavLinks) {
      return getMarketingRoutes(features);
    }
  } else {
    const appRoutes = getAppRoutes(features);
    // logged out
    if (_.isEmpty(user)) {
      return [
        appRoutes.home,
        appRoutes.community,
      ];
    }
    // logged in

    // restricted
    if (isRestricted) return [ appRoutes.home ];

    // unrestricted
    return [
      appRoutes.home,
      appRoutes.catalog,
      appRoutes.shop,
      appRoutes.community,
      appRoutes.support,
    ];
  }
};



// WEBPACK FOOTER //
// ./app/common/components/navigation/config.js